import { LOCALIZED } from '@libs/core';
import { getProductUrl } from '@libs/algolia';
import { JsonLd } from '@libs/common-next';

import type { Product } from '@libs/common-next';
import type { ProductGridSeoProps } from './types';
import type { ProductHitPrice } from '@libs/algolia';
import type {
    CollectionPage,
    ListItem,
    Product as ProductSchema,
    WithContext,
} from 'schema-dts';

const IN_STOCK = 'https://schema.org/InStock';
const OUT_OF_STOCK = 'https://schema.org/OutOfStock';

export default function ProductGridSeo({
    seoMetadata,
    products,
}: ProductGridSeoProps) {
    const { metaTitle, metaDescription } = seoMetadata;

    const itemListElement = products
        .filter(
            (product?: Product) =>
                // Filter out products with nullish rawProductHit or missing price
                product && product.rawProductHit && product.rawProductHit.price
        )
        .map((product: Product, index: number): ListItem => {
            const productSchema: ProductSchema = {
                '@type': 'Product',
                name: product.title,
                image: `https://s7d2.scene7.com/is/image/PetSmart/${product.id}`,
                description: product.title,
                sku: String(product.id),
                brand: {
                    '@type': 'Brand',
                    name: product.brand,
                },
                offers: {
                    '@type': 'Offer',
                    priceCurrency: LOCALIZED.currencyCode,
                    price: String(
                        (product.rawProductHit.price as ProductHitPrice).number
                    ),
                    priceValidUntil: '9999-12-31T00:00:00.000Z',
                    availability:
                        product.rawProductHit.in_stock === false
                            ? OUT_OF_STOCK
                            : IN_STOCK,
                    url: `${LOCALIZED.hostname}${getProductUrl(
                        product.rawProductHit
                    )}`,
                },
            };

            // Only add aggregateRating object if we have one or more reviews
            if (product.rawProductHit.bvReviewCount) {
                productSchema.aggregateRating = {
                    '@type': 'AggregateRating',
                    ratingValue: product.rawProductHit.bvAverageRating,
                    reviewCount: product.rawProductHit.bvReviewCount,
                    bestRating: '5',
                    worstRating: '1',
                };
            }

            return {
                '@type': 'ListItem',
                position: index + 1,
                item: productSchema,
            };
        });

    const jsonLd: WithContext<CollectionPage> = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: metaTitle,
        description: metaDescription,
        mainEntity: {
            '@type': 'ItemList',
            itemListElement,
        },
    };

    return (
        <JsonLd
            key="plp-collections-seo"
            id="plp-collections-seo"
            data-testid="plp-collections-seo"
            json={jsonLd}
        />
    );
}
